/* Pinelume storefront behaviour. Loaded as a module after Horizon's own scripts.
   Everything here enhances server-rendered markup; the page works without it. */
import { QuantitySelectorUpdateEvent } from '@theme/events';

const locale = document.documentElement.lang || 'en';
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- business-day maths (delivery estimates) ---------- */
function addBusinessDays(from, days) {
  const d = new Date(from);
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    const wd = d.getDay();
    if (wd !== 0 && wd !== 6) added += 1;
  }
  return d;
}

const fmtDay = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' });

const fmtDate = new Intl.DateTimeFormat(locale, { day: 'numeric' });

/* "Sep 21–23" inside one month, "Sep 30 – Oct 9" across two: short enough for a phone. */
function formatRange(a, b) {
  const left = fmtDay.format(a);
  if (a.toDateString() === b.toDateString()) return left;
  if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()) {
    return `${left}–${fmtDate.format(b)}`;
  }
  return `${left} – ${fmtDay.format(b)}`;
}

function renderDelivery(root) {
  const n = (k) => parseInt(root.dataset[k], 10) || 0;
  const today = new Date();
  const shipA = addBusinessDays(today, n('procMin'));
  const shipB = addBusinessDays(today, n('procMax'));
  const arriveA = addBusinessDays(shipA, n('transitMin'));
  const arriveB = addBusinessDays(shipB, n('transitMax'));

  const values = {
    today: fmtDay.format(today),
    ship: formatRange(shipA, shipB),
    arrive: formatRange(arriveA, arriveB),
  };

  root.querySelectorAll('[data-pl-date]').forEach((el) => {
    const value = values[el.dataset.plDate];
    /* Compare first: an unconditional write would re-trigger the observer below forever. */
    if (value && el.textContent !== value) el.textContent = value;
  });
}

/* Horizon morphs the product section on variant change, which puts the server's
   fallback text back; watch each timeline and re-apply the dates. */
function watchDelivery(root) {
  renderDelivery(root);
  if (root.dataset.plWatching) return;
  root.dataset.plWatching = '1';
  new MutationObserver(() => renderDelivery(root)).observe(root, {
    childList: true,
    subtree: true,
    characterData: true,
  });
}

/* ---------- christmas cut-off countdown (real date from theme settings) ---------- */
function renderCutoff(root) {
  const deadline = new Date(root.dataset.deadline);
  if (Number.isNaN(deadline.getTime())) return;
  const out = root.querySelector('[data-pl-countdown]');

  const tick = () => {
    const ms = deadline.getTime() - Date.now();
    if (ms <= 0) {
      root.hidden = true;
      return false;
    }
    root.hidden = false;
    const days = Math.floor(ms / 86400000);
    const hours = Math.floor((ms % 86400000) / 3600000);
    const mins = Math.floor((ms % 3600000) / 60000);
    if (out) out.textContent = days > 0 ? `${days}d ${hours}h` : `${hours}h ${mins}m`;
    return true;
  };

  if (tick()) setInterval(tick, 60000);
}

/* ---------- bundle picker -> Horizon quantity selector ---------- */
function productFormFor(el) {
  const section = el.closest('.shopify-section') || document;
  return section.querySelector('product-form-component');
}

function setFormQuantity(bundle, qty) {
  const form = productFormFor(bundle);
  const selector = form?.querySelector('quantity-selector-component');
  if (selector && typeof selector.setValue === 'function') {
    selector.setValue(String(qty));
    selector.updateButtonStates?.();
    selector.onQuantityChange?.();
  } else {
    const input = form?.querySelector('input[name="quantity"]');
    if (input) input.value = String(qty);
  }
}

function initBundle(bundle) {
  const radios = [...bundle.querySelectorAll('input[type="radio"]')];
  if (!radios.length) return;

  const summary = bundle.querySelector('[data-pl-bundle-summary]');
  const apply = () => {
    const picked = radios.find((r) => r.checked);
    if (!picked) return;
    setFormQuantity(bundle, parseInt(picked.value, 10) || 1);
    if (summary && picked.dataset.summary) summary.innerHTML = picked.dataset.summary;
  };

  radios.forEach((r) => r.addEventListener('change', apply));

  const wanted = new URLSearchParams(location.search).get('bundle');
  const preset = radios.find((r) => r.value === wanted);
  if (preset) preset.checked = true;

  /* Quantity selector exists only after Horizon upgrades it. */
  customElements.whenDefined('quantity-selector-component').then(apply);

  /* Keep the cards honest if someone edits quantity elsewhere (sticky bar, +/-). */
  const section = bundle.closest('.shopify-section');
  section?.addEventListener('quantity-selector:update', (event) => {
    if (bundle.contains(event.target)) return;
    const qty = Number(event.detail?.quantity);
    if (!qty || event.detail?.cartLine) return;
    const match = radios.find((r) => Number(r.value) === qty);
    radios.forEach((r) => (r.checked = r === match));
    if (summary) summary.innerHTML = match && match.dataset.summary ? match.dataset.summary : '';
  });
}

/* Links like <a data-pl-pick="2" href="#pl-buy"> pick a bundle and scroll to the buy box. */
document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-pl-pick]');
  if (!link) return;
  const radio = document.querySelector(`.pl-bundle input[value="${link.dataset.plPick}"]`);
  if (!radio) return;
  event.preventDefault();
  radio.checked = true;
  radio.dispatchEvent(new Event('change', { bubbles: true }));
  const target = radio.closest('.pl-bundle');
  target?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
  radio.focus({ preventScroll: true });
});

/* ---------- cart: "add another set" uses Horizon's own line update path ---------- */
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-pl-bump]');
  if (!button) return;
  event.preventDefault();
  const line = parseInt(button.dataset.line, 10);
  const qty = parseInt(button.dataset.qty, 10);
  if (!line || !qty) return;
  button.setAttribute('aria-busy', 'true');
  button.dispatchEvent(new QuantitySelectorUpdateEvent(qty, line));
});

/* ---------- home: sticky mobile CTA once the hero leaves the screen ---------- */
function initSticky(bar) {
  const watch = document.querySelector(bar.dataset.watch || '.pl-hero');
  const footer = document.querySelector('.pl-footer');
  if (!watch || !('IntersectionObserver' in window)) return;
  let heroGone = false;
  let footerIn = false;
  const sync = () => bar.classList.toggle('is-visible', heroGone && !footerIn);
  new IntersectionObserver(([e]) => { heroGone = !e.isIntersecting; sync(); }).observe(watch);
  if (footer) new IntersectionObserver(([e]) => { footerIn = e.isIntersecting; sync(); }).observe(footer);
}

/* ---------- decorative motion only while it is on screen ---------- */
const liveObserver = 'IntersectionObserver' in window && !reducedMotion
  ? new IntersectionObserver((entries) => {
      entries.forEach((e) => e.target.classList.toggle('is-live', e.isIntersecting));
    }, { rootMargin: '80px 0px' })
  : null;

/* ---------- boot (also re-runs for editor + section re-renders) ---------- */
function boot(scope = document) {
  scope.querySelectorAll('[data-pl-delivery]').forEach(watchDelivery);
  scope.querySelectorAll('[data-pl-cutoff]').forEach((c) => {
    if (c.dataset.plReady) return;
    c.dataset.plReady = '1';
    renderCutoff(c);
  });
  scope.querySelectorAll('.pl-bundle').forEach((b) => {
    if (b.dataset.plReady) return;
    b.dataset.plReady = '1';
    initBundle(b);
  });
  if (liveObserver) {
    scope.querySelectorAll('.pl-lights, .pl-snowfall, .pl-announce').forEach((el) => {
      if (el.dataset.plLive) return;
      el.dataset.plLive = '1';
      liveObserver.observe(el);
    });
  }
  scope.querySelectorAll('.pl-sticky').forEach((s) => {
    if (s.dataset.plReady) return;
    s.dataset.plReady = '1';
    initSticky(s);
  });
}

boot();
document.addEventListener('shopify:section:load', (e) => boot(e.target));
